import {
  Box,
  Typography,
  Slider,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import theme from "../theme";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

// Icons
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VideoFileRoundedIcon from "@mui/icons-material/VideoFileRounded";
import TextSnippetRoundedIcon from "@mui/icons-material/TextSnippetRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import CloseIcon from "@mui/icons-material/Close";

const TicketDetails = ({ ticketData }) => {
  const resTheme = useTheme();
  const isMobile = useMediaQuery(resTheme.breakpoints.down("md"));
  const ticket = ticketData.ticket;

  const [overlayFile, setOverlayFile] = useState(null);
  const [overlayIndex, setOverlayIndex] = useState(null);
  // Audio element reference
  const audioRef = useRef(null);

  // State variables
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);

  // 🔹 Extract extension and filename from URL
  const getFileMeta = (url) => {
    const filename = url.split("/").pop();
    const ext = filename.split(".").pop().toLowerCase();

    let type = "other";
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) type = "image";
    else if (["mp3", "wav", "ogg", "m4a"].includes(ext)) type = "audio";
    else if (ext === "webm") type = "audio"; // ✅ treat recorded webm as audio
    else if (["mp4", "mkv"].includes(ext)) type = "video";
    else if (ext === "pdf") type = "pdf";
    else if (["txt", "csv", "doc", "docx"].includes(ext)) type = "text";

    return { filename, type, ext };
  };

  // Setup audio
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => setProgress(audio.currentTime);
    const setMeta = () => setDuration(audio.duration);

    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("loadedmetadata", setMeta);

    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("loadedmetadata", setMeta);
    };
  }, []);

  // helper to detect the single audio file
  const isAudioUrl = (url) => {
    const ext = url.split(".").pop().toLowerCase().split("?")[0];
    return ["mp3", "wav", "ogg", "m4a", "webm"].includes(ext);
  };

  // pick the only audio file
  const audioUrl = ticket?.media?.find(isAudioUrl) || "";

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) audio.pause();
    else audio.play();
    setIsPlaying(!isPlaying);
  };

  const handleSliderChange = (_, value) => {
    audioRef.current.currentTime = value;
    setProgress(value);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  // Media file downloader
  const downloadFile = async (url, filename) => {
    try {
      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) throw new Error("Network response not ok");
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("Download failed:", err);
      // Fallback: open in new tab so the user can save manually
      window.open(url, "_blank", "noopener");
    }
  };

  const handleOpenOverlay = (url, meta, index) => {
    setOverlayFile({ url, ...meta });
    setOverlayIndex(index);
  };

  const handleNext = () => {
    if (overlayIndex === null) return;
    const mediaArray = ticket.media.filter((url) => !isAudioUrl(url));
    const nextIndex = (overlayIndex + 1) % mediaArray.length;
    const nextUrl = mediaArray[nextIndex];
    const meta = getFileMeta(nextUrl);
    setOverlayFile({ url: nextUrl, ...meta });
    setOverlayIndex(nextIndex);
  };

  const handlePrev = () => {
    if (overlayIndex === null) return;
    const mediaArray = ticket.media.filter((url) => !isAudioUrl(url));
    const prevIndex =
      (overlayIndex - 1 + mediaArray.length) % mediaArray.length;
    const prevUrl = mediaArray[prevIndex];
    const meta = getFileMeta(prevUrl);
    setOverlayFile({ url: prevUrl, ...meta });
    setOverlayIndex(prevIndex);
  };

  return (
    // <Box sx={{ width: { sx: "100%", md: "45%" } }}>
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          // border: `2px solid #4428A0`,
          borderRadius: 3,
        }}
      >
        {/* Ticket Description */}
        <Box
          sx={{
            bgcolor: "#F7F9FC",
            p: 2,
            borderRadius: 2,
            border: "1px solid #E5EAF2",
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            Description
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#555555" }}>
            {ticket?.description}
          </Typography>
        </Box>

        {/* Attachments Heading (only show if there are some attachments) */}
        {ticket?.media?.length > 0 && (
          <Box sx={{ bgcolor: "#E1EFFF", p: 2, borderRadius: 2, mt: 2 }}>
            <>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                Attachments
              </Typography>{" "}
              {ticket?.media?.length > 0 && (
                // AFTER
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: 1.5,
                    mt: 2,
                    mb: 1,
                  }}
                >
                  {(() => {
                    // Defensive parsing
                    let mediaArray = [];
                    try {
                      mediaArray = Array.isArray(ticket.media)
                        ? ticket.media
                        : JSON.parse(ticket.media || "[]");
                    } catch (err) {
                      console.error("Failed to parse media array", err);
                    }

                    return mediaArray
                      .filter((url) => !isAudioUrl(url))
                      .map((url, index) => {
                        const meta = getFileMeta(url);
                        return (
                          <Box
                            key={index}
                            sx={{
                              position: "relative",
                              width: "100%",
                              aspectRatio: "1 / 1",
                              borderRadius: 1.5,
                              backgroundImage:
                                meta.type === "image" ? `url(${url})` : "none",
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                              cursor: "pointer",
                            }}
                            onClick={() => handleOpenOverlay(url, meta, index)}
                          >
                            {meta.type === "text" || meta.type === "pdf" ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexDirection: "column",
                                  width: "100%",
                                  height: "100%",
                                  border: `1px solid ${theme.lightTheme.themeCol}`,
                                  borderRadius: 3,
                                }}
                              >
                                <TextSnippetRoundedIcon
                                  sx={{
                                    width: 56,
                                    height: 64,
                                    fill: theme.button.primaryBtn,
                                  }}
                                />
                              </Box>
                            ) : null}

                            {meta.type === "video" && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexDirection: "column",
                                  width: "100%",
                                  height: "100%",
                                  border: `1px solid ${theme.lightTheme.themeCol}`,
                                  borderRadius: 3,
                                }}
                              >
                                <VideoFileRoundedIcon
                                  sx={{
                                    width: 56,
                                    height: 64,
                                    fill: theme.button.primaryBtn,
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        );
                      });
                  })()}
                </Box>
              )}
            </>
          </Box>
        )}

        {/* Audio Player */}
        {audioUrl && (
          <Box sx={{ bgcolor: "#E1EFFF", p: 2, borderRadius: 2, mt: 2 }}>
            {" "}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",

                borderRadius: 2,
              }}
            >
              <audio ref={audioRef} src={audioUrl} />
              <IconButton onClick={togglePlay}>
                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
              <Slider
                min={0}
                max={duration}
                value={progress}
                onChange={handleSliderChange}
                sx={{
                  flex: 1,
                  color: theme.button.primaryBtn,
                  "& .MuiSlider-thumb": {
                    backgroundColor: theme.button.primaryBtn,
                  },
                }}
              />
              <IconButton onClick={toggleMute}>
                {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
              </IconButton>
              <Typography variant="caption">
                {Math.floor(progress)}s / {Math.floor(duration)}s
              </Typography>
            </Box>
          </Box>
        )}

        {/* Ticket Status, Priority & Time Budget */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            mt: 2,
          }}
        >
          {/* Ticket Status */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 1.5,
              py: 1,
              bgcolor: "#F4FFF3",
              border: `1px solid #1D8B19`,
              borderRadius: 7.5,
            }}
          >
            <Typography sx={{ fontSize: 14 }}>
              {ticketData?.ticket?.status === "open" && "Open"}
              {ticketData?.ticket?.status === "in_progress" && "Pending"}
              {ticketData?.ticket?.status === "resolved" && "Resolved"}
              {ticketData?.ticket?.status === "follow_up" && "Follow-Up"}
              {ticketData?.ticket?.status === "recurring" && "Recurring"}
              {ticketData?.ticket?.status === "rejected" && "Rejected"}
            </Typography>
          </Box>

          {/* Priority */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 1.5,
              py: 1,
              bgcolor:
                theme.lightTheme.priorities[ticketData?.ticket?.priority].bgCol,
              border: `1px solid ${
                theme.lightTheme.priorities[ticketData?.ticket?.priority]
                  .textCol
              }`,
              borderRadius: 7.5,
            }}
          >
            <Typography sx={{ fontSize: 14 }}>
              {ticketData?.ticket?.priority === "low" && "Low"}
              {ticketData?.ticket?.priority === "medium" && "Medium"}
              {ticketData?.ticket?.priority === "high" && "High"}
            </Typography>
          </Box>

          {/* Time Budget */}
          {ticketData?.ticket_info.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 1.5,
                py: 1,
                bgcolor: "#FFF5F5",
                border: `1px solid #FF474A`,
                borderRadius: 7.5,
              }}
            >
              <Typography sx={{ fontSize: 14 }}>
                {ticketData?.ticket_info[0]?.time_budget} min
              </Typography>
            </Box>
          )}
        </Box>

        {/* Fullscreen Media View */}
        {overlayFile && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0,0,0,0.8)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: isMobile ? 999 : 99999,
            }}
            onClick={() => setOverlayFile(null)}
          >
            {/* ❌ Close Button */}
            <IconButton
              onClick={(e) => {
                e.stopPropagation(); // Prevent closing by overlay click
                setOverlayFile(null);
              }}
              sx={{
                position: "absolute",
                top: 16,
                left: 16,
                color: "#FFF",
                bgcolor: "rgba(165, 165, 165, 0.4)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
              }}
            >
              <CloseIcon />
            </IconButton>{" "}
            {/* Navigation Arrows */}
            {ticket?.media?.filter((url) => !isAudioUrl(url)).length > 1 && (
              <>
                {/* Prev Arrow */}
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation(); // ✅ prevent overlay close
                    handlePrev();
                  }}
                  sx={{
                    position: "absolute",
                    left: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#FFF",
                    bgcolor: "rgba(0,0,0,0.4)",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                  }}
                >
                  <ArrowForwardIosRoundedIcon
                    sx={{ transform: "rotate(180deg)" }}
                  />
                </IconButton>

                {/* Next Arrow */}
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation(); // ✅ prevent overlay close
                    handleNext();
                  }}
                  sx={{
                    position: "absolute",
                    right: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#FFF",
                    bgcolor: "rgba(0,0,0,0.4)",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                  }}
                >
                  <ArrowForwardIosRoundedIcon />
                </IconButton>
              </>
            )}
            <Box
              sx={{
                position: "relative",
                maxWidth: "95vw",
                maxHeight: "95vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* IMAGE */}
              {overlayFile?.type === "image" && (
                <TransformWrapper
                  initialScale={1}
                  minScale={1}
                  wheel={{ step: 0.1 }}
                  pinch={{ step: 5 }}
                  doubleClick={{ disabled: false, step: 1.5 }}
                >
                  <TransformComponent>
                    <img
                      src={overlayFile.url}
                      alt={overlayFile.filename}
                      style={{
                        maxWidth: "95vw",
                        maxHeight: "85vh", // leave space for button
                        display: "block",
                      }}
                    />
                  </TransformComponent>
                </TransformWrapper>
              )}

              {/* VIDEO */}
              {overlayFile?.type === "video" && (
                <video
                  controls
                  style={{
                    maxWidth: "95vw",
                    maxHeight: "85vh",
                    display: "block",
                  }}
                >
                  <source
                    src={overlayFile.url}
                    type={`video/${overlayFile.ext}`}
                  />
                </video>
              )}

              {/* PDF */}
              {overlayFile?.type === "pdf" && (
                <object
                  data={overlayFile.url}
                  type="application/pdf"
                  style={{
                    width: isMobile ? "100%" : "95vw",
                    height: isMobile ? "100%" : "85vh",
                    backgroundColor: isMobile ? "#FFF" : "none",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "column",
                      width: "100%",
                    }}
                  >
                    <Typography>PDF preview not supported.</Typography>
                    <Typography
                      onClick={async () =>
                        downloadFile(overlayFile.url, overlayFile.filename)
                      }
                    >
                      Click{" "}
                      <span
                        style={{ color: "blue", textDecoration: "underline" }}
                      >
                        here
                      </span>{" "}
                      to download the PDF
                    </Typography>
                  </Box>
                </object>
              )}

              {/* Download Button */}
              {overlayFile?.type !== "pdf" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    bgcolor: "#FFF",
                    position: "absolute", // ✅ anchor inside parent
                    top: 8,
                    right: 8,
                    boxShadow: 2,
                    cursor: "pointer",
                  }}
                  onClick={async () =>
                    downloadFile(overlayFile.url, overlayFile.filename)
                  }
                >
                  <FileDownloadRoundedIcon
                    sx={{ fontSize: 24, fill: theme.lightTheme.themeCol }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TicketDetails;
